import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { PageHeader, EmptyState, Modal, Button, inr } from "../components/UI";
import { Repeat, Sparkles, Phone, MessageCircle, Clock, AlertTriangle, Calendar, TrendingDown, Copy, CheckCircle2, XCircle, Timer, PhoneOff } from "lucide-react";
import { toast } from "sonner";

const urgencyMeta = {
  overdue: { label: "Overdue", cls: "bg-red-100 text-red-800 border-red-200", dot: "bg-red-600", icon: AlertTriangle },
  urgent: { label: "Call Now", cls: "bg-orange-100 text-orange-800 border-orange-200", dot: "bg-orange-500", icon: Phone },
  upcoming: { label: "Upcoming", cls: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-500", icon: Calendar },
  future: { label: "Future", cls: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400", icon: Clock },
};

const confidenceMeta = {
  high: { label: "High confidence", color: "text-green-700" },
  medium: { label: "Medium confidence", color: "text-amber-700" },
  low: { label: "Low confidence", color: "text-slate-500" },
};

export default function Reorders() {
  const [data, setData] = useState({ predictions: [], summary: {} });
  const [effectiveness, setEffectiveness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [activePred, setActivePred] = useState(null);
  const [reminderId, setReminderId] = useState(null);

  // Outcome capture
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [outcomeForm, setOutcomeForm] = useState({ outcome: "", expected_order_date: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [r, e] = await Promise.all([
        api.get("/reorder/predictions"),
        api.get("/reorder/effectiveness").catch(() => ({ data: null })),
      ]);
      setData(r.data);
      setEffectiveness(e.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? data.predictions : data.predictions.filter((p) => p.urgency === filter);

  const generateAI = async (p) => {
    setActivePred(p);
    setAiOpen(true);
    setAiResult(null);
    setAiLoading(true);
    setReminderId(null);
    // Log the reminder and capture id for outcome linking
    try {
      const logRes = await api.post("/reorder/reminders", {
        customer_id: p.customer_id,
        customer_name: p.customer_name,
        product_name: p.product_name,
        predicted_next_order_date: p.predicted_next_order_date,
        avg_gap_days: p.avg_gap_days,
        last_quantity: p.last_quantity,
      });
      setReminderId(logRes.data?.id || null);
    } catch (_) { /* non-blocking */ }
    try {
      const r = await api.post("/reorder/ai-insight", {
        customer_name: p.customer_name,
        product_name: p.product_name,
        last_order_date: p.last_order_date,
        last_quantity: p.last_quantity,
        avg_gap_days: p.avg_gap_days,
        days_until_reorder: p.days_until_reorder,
        estimated_remaining_qty: p.estimated_remaining_qty,
        customer_phone: p.customer_phone,
        customer_type: p.customer_type,
      });
      setAiResult(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
      setAiOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const openOutcome = (outcome) => {
    // Pre-fill default expected_order_date for will_order / maybe_later
    const today = new Date();
    let defaultDate = "";
    if (outcome === "will_order") {
      const d = new Date(today); d.setDate(d.getDate() + 3);
      defaultDate = d.toISOString().slice(0, 10);
    } else if (outcome === "maybe_later") {
      const d = new Date(today); d.setDate(d.getDate() + 14);
      defaultDate = d.toISOString().slice(0, 10);
    }
    setOutcomeForm({ outcome, expected_order_date: defaultDate, notes: "" });
    setOutcomeOpen(true);
  };

  const saveOutcome = async () => {
    if (!activePred) return;
    if (["will_order", "maybe_later"].includes(outcomeForm.outcome) && !outcomeForm.expected_order_date) {
      toast.error("Expected order date is required for this outcome");
      return;
    }
    setSavingOutcome(true);
    try {
      const r = await api.post("/reorder/log-outcome", {
        customer_id: activePred.customer_id,
        product_name: activePred.product_name,
        outcome: outcomeForm.outcome,
        expected_order_date: outcomeForm.expected_order_date || null,
        notes: outcomeForm.notes,
        reminder_id: reminderId,
      });
      toast.success(r.data?.message || "Outcome saved");
      setOutcomeOpen(false);
      setAiOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingOutcome(false);
    }
  };

  const total = (data.summary.overdue || 0) + (data.summary.urgent || 0);

  return (
    <div className="p-4 lg:p-8 max-w-7xl" data-testid="reorders-page">
      <PageHeader
        title="Reorder Radar"
        subtitle="AI-powered predictions of when your customers will run out and need to reorder"
      />

      {/* Effectiveness card */}
      {effectiveness && effectiveness.total_reminders > 0 && (
        <div className="card-flat p-4 mb-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="effectiveness">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">Reminders Sent</div>
            <div className="font-display font-bold text-2xl mt-1">{effectiveness.total_reminders}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">Converted to Orders</div>
            <div className="font-display font-bold text-2xl mt-1 text-green-700">{effectiveness.converted}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">Conversion Rate</div>
            <div className="font-display font-bold text-2xl mt-1 text-[#C1121F]">{effectiveness.conversion_rate}%</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">Pending Follow-up</div>
            <div className="font-display font-bold text-2xl mt-1 text-amber-600">{effectiveness.pending}</div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 stagger">
        {["overdue", "urgent", "upcoming", "future"].map((k) => {
          const meta = urgencyMeta[k];
          const Icon = meta.icon;
          return (
            <div key={k} className="card-flat p-4 flex items-center gap-3" data-testid={`reorder-summary-${k}`}>
              <div className={`w-10 h-10 rounded-md flex items-center justify-center ${meta.cls.split(" ").slice(0, 1).join("")}`}>
                <Icon size={18} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">{meta.label}</div>
                <div className="font-display font-bold text-2xl text-[#0F172A]">{data.summary[k] || 0}</div>
              </div>
            </div>
          );
        })}
      </div>

      {total > 0 && (
        <div className="mb-5 p-4 bg-gradient-to-r from-[#FFF5F3] to-[#FFE8E3] border border-[#FECACA] rounded-lg flex items-start gap-3" data-testid="reorder-alert">
          <Sparkles className="text-[#C1121F] flex-shrink-0 mt-0.5" size={20} />
          <div>
            <div className="font-display font-bold text-[#C1121F]">
              {total} customer{total !== 1 ? "s" : ""} ready to reorder
            </div>
            <div className="text-sm text-[#475569] mt-0.5">
              Call them now with an AI-generated Hinglish script to lock in repeat orders.
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-5">
        {["all", "overdue", "urgent", "upcoming", "future"].map((k) => (
          <button
            key={k}
            data-testid={`reorder-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold transition ${
              filter === k ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569] hover:border-[#C1121F]"
            }`}
          >
            {k}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#475569]">Analyzing order history…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Repeat}
          title="No predictions yet"
          hint="Once you have orders for multiple customers, the AI will predict when each customer's stock runs out and they're ready for a repeat order."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reorders-grid">
          {filtered.map((p) => {
            const meta = urgencyMeta[p.urgency];
            const conf = confidenceMeta[p.confidence];
            return (
              <div key={p.id} className="card-flat p-5 relative overflow-hidden" data-testid={`reorder-card-${p.id}`}>
                <div className={`absolute top-0 left-0 right-0 h-1 ${meta.dot}`} />
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${meta.cls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
                      {meta.label}
                    </span>
                  </div>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${conf.color}`}>
                    {conf.label}
                  </span>
                </div>

                <h3 className="font-display font-bold text-lg leading-tight">{p.customer_name}</h3>
                <div className="text-sm text-[#475569] mt-0.5">{p.product_name}</div>

                <div className="grid grid-cols-2 gap-3 mt-4 pb-4 border-b border-[#E5E7EB]">
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-[#94A3B8] font-semibold">Predicted Reorder</div>
                    <div className="font-mono-num font-bold text-sm mt-0.5">{p.predicted_next_order_date}</div>
                    <div className={`text-xs mt-0.5 font-semibold ${
                      p.days_until_reorder <= 0 ? "text-red-600" : p.days_until_reorder <= 10 ? "text-orange-600" : "text-[#475569]"
                    }`}>
                      {p.days_until_reorder <= 0 ? `${Math.abs(p.days_until_reorder)} days overdue` : `in ${p.days_until_reorder} days`}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-[#94A3B8] font-semibold">Est. Remaining</div>
                    <div className="font-mono-num font-bold text-sm mt-0.5">{p.estimated_remaining_qty} pcs</div>
                    <div className="text-xs text-[#475569] mt-0.5 flex items-center gap-1">
                      <TrendingDown size={11}/> {p.daily_consumption}/day
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-[#94A3B8] font-semibold">Orders</div>
                    <div className="font-mono-num font-semibold">{p.order_count}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-[#94A3B8] font-semibold">Avg Gap</div>
                    <div className="font-mono-num font-semibold">{p.avg_gap_days}d</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-[#94A3B8] font-semibold">Last Qty</div>
                    <div className="font-mono-num font-semibold">{p.last_quantity}</div>
                  </div>
                </div>

                <button
                  onClick={() => generateAI(p)}
                  data-testid={`generate-ai-${p.id}`}
                  className="mt-4 w-full btn-primary py-2 rounded font-semibold text-sm flex items-center justify-center gap-2"
                >
                  <Sparkles size={14}/> Generate AI Call Script
                </button>

                {p.customer_phone && (
                  <a
                    href={`tel:${p.customer_phone}`}
                    className="mt-2 w-full block text-center py-2 rounded text-xs uppercase tracking-wider font-semibold text-[#475569] border border-[#E5E7EB] hover:border-[#C1121F] hover:text-[#C1121F]"
                  >
                    <Phone size={11} className="inline mr-1.5"/> {p.customer_phone}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal open={aiOpen} onClose={() => setAiOpen(false)} title="AI Sales Assistant" maxWidth="max-w-2xl">
        {aiLoading ? (
          <div className="py-10 text-center" data-testid="ai-loading">
            <div className="inline-flex w-12 h-12 bg-[#FEF2F2] rounded-full items-center justify-center animate-pulse">
              <Sparkles className="text-[#C1121F]" size={20}/>
            </div>
            <div className="mt-3 font-display font-semibold text-[#0F172A]">Crafting your script…</div>
            <div className="text-sm text-[#475569] mt-1">Gemini 3 Flash is analyzing the customer pattern.</div>
          </div>
        ) : aiResult ? (
          <div className="space-y-4" data-testid="ai-result">
            {activePred && (
              <div className="text-xs text-[#475569] -mt-2 mb-1">
                For: <span className="font-semibold text-[#0F172A]">{activePred.customer_name}</span> · <span className="font-mono-num">{activePred.product_name}</span>
              </div>
            )}
            {aiResult.key_insight && (
              <div className="p-3 bg-[#FEF2F2] border border-[#FECACA] rounded">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#C1121F]">Insight</div>
                <div className="text-sm text-[#0F172A] mt-1">{aiResult.key_insight}</div>
              </div>
            )}

            <div className="card-flat p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2"><Phone size={15} className="text-[#C1121F]"/><div className="text-[10px] uppercase tracking-wider font-bold text-[#475569]">Phone Call Script</div></div>
                <button onClick={() => copy(aiResult.call_script)} data-testid="copy-script" className="text-xs text-[#475569] hover:text-[#C1121F] flex items-center gap-1"><Copy size={12}/> Copy</button>
              </div>
              <div className="text-sm text-[#0F172A] whitespace-pre-wrap leading-relaxed">{aiResult.call_script}</div>
            </div>

            <div className="card-flat p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2"><MessageCircle size={15} className="text-green-600"/><div className="text-[10px] uppercase tracking-wider font-bold text-[#475569]">WhatsApp Message</div></div>
                <div className="flex gap-2">
                  <button onClick={() => copy(aiResult.whatsapp_message)} data-testid="copy-wa" className="text-xs text-[#475569] hover:text-[#C1121F] flex items-center gap-1"><Copy size={12}/> Copy</button>
                  {activePred?.customer_phone && (
                    <a
                      href={`https://wa.me/${activePred.customer_phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(aiResult.whatsapp_message)}`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid="send-wa"
                      className="text-xs text-green-700 hover:text-green-800 flex items-center gap-1 font-semibold"
                    >Send →</a>
                  )}
                </div>
              </div>
              <div className="text-sm text-[#0F172A] whitespace-pre-wrap leading-relaxed">{aiResult.whatsapp_message}</div>
            </div>

            {aiResult.suggested_offer && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800">Suggested Offer</div>
                <div className="text-sm text-[#0F172A] mt-1">{aiResult.suggested_offer}</div>
              </div>
            )}

            {/* Outcome Capture — sales staff logs the call result here */}
            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#475569] mb-2">Customer ka jawab kya tha?</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="outcome-buttons">
                <button
                  onClick={() => openOutcome("will_order")}
                  data-testid="outcome-will-order"
                  className="px-2 py-2 bg-green-50 border-2 border-green-200 text-green-800 rounded text-xs font-semibold hover:bg-green-100 flex flex-col items-center gap-1"
                >
                  <CheckCircle2 size={16}/>
                  Order Dega
                </button>
                <button
                  onClick={() => openOutcome("maybe_later")}
                  data-testid="outcome-maybe-later"
                  className="px-2 py-2 bg-amber-50 border-2 border-amber-200 text-amber-800 rounded text-xs font-semibold hover:bg-amber-100 flex flex-col items-center gap-1"
                >
                  <Timer size={16}/>
                  Baad mein
                </button>
                <button
                  onClick={() => openOutcome("no_response")}
                  data-testid="outcome-no-response"
                  className="px-2 py-2 bg-slate-50 border-2 border-slate-200 text-slate-700 rounded text-xs font-semibold hover:bg-slate-100 flex flex-col items-center gap-1"
                >
                  <PhoneOff size={16}/>
                  No Response
                </button>
                <button
                  onClick={() => openOutcome("not_interested")}
                  data-testid="outcome-not-interested"
                  className="px-2 py-2 bg-red-50 border-2 border-red-200 text-red-800 rounded text-xs font-semibold hover:bg-red-100 flex flex-col items-center gap-1"
                >
                  <XCircle size={16}/>
                  Not Interested
                </button>
              </div>
              <div className="text-[10px] text-[#94A3B8] mt-2">
                💡 &quot;Order Dega&quot; ya &quot;Baad mein&quot; click karne pe automatically Follow-up bann jayega expected date pe.
              </div>
            </div>

            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setAiOpen(false)}>Close</Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Outcome Confirmation Modal */}
      <Modal open={outcomeOpen} onClose={() => !savingOutcome && setOutcomeOpen(false)} title="Outcome Save Karein" maxWidth="max-w-md">
        <div className="space-y-3" data-testid="outcome-modal">
          {activePred && (
            <div className="text-xs text-[#475569] p-2 bg-[#F8F9FA] rounded">
              <b>{activePred.customer_name}</b> · {activePred.product_name}
            </div>
          )}
          <div className="text-sm">
            Outcome: <span className="font-bold text-[#C1121F]">
              {outcomeForm.outcome === "will_order" && "✓ Order Dega"}
              {outcomeForm.outcome === "maybe_later" && "⏰ Baad mein order karega"}
              {outcomeForm.outcome === "no_response" && "📵 Phone uthaaya nahi"}
              {outcomeForm.outcome === "not_interested" && "✗ Not interested"}
            </span>
          </div>
          {["will_order", "maybe_later"].includes(outcomeForm.outcome) && (
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#475569] mb-1 block">
                Expected Order Date <span className="text-[#C1121F]">*</span>
              </label>
              <input
                type="date"
                value={outcomeForm.expected_order_date}
                onChange={(e) => setOutcomeForm({ ...outcomeForm, expected_order_date: e.target.value })}
                className="input-base"
                data-testid="outcome-date"
                required
              />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#475569] mb-1 block">Notes (optional)</label>
            <textarea
              value={outcomeForm.notes}
              onChange={(e) => setOutcomeForm({ ...outcomeForm, notes: e.target.value })}
              rows={2}
              placeholder="e.g. 'Bola Friday tak quote bhej do, fir confirm karenge'"
              className="input-base"
              data-testid="outcome-notes"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setOutcomeOpen(false)} disabled={savingOutcome}>Cancel</Button>
            <Button onClick={saveOutcome} disabled={savingOutcome} data-testid="save-outcome-btn">
              {savingOutcome ? "Saving…" : "Save Outcome"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
